import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { HttpClient } from '@angular/common/http';
import { GenericCRUDService } from '../shared/services';
import { Data } from '../shared/models';
import { Dio, DioDetalhesExecucao } from '../dio/dio.model';
import { VBBFilters } from './valor-bruto-beneficiario/valor-bruto-beneficiario.component';
import { GenerateTablePagination } from '../shared/components';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class RelatorioService extends GenericCRUDService {

  constructor(http: HttpClient) {
    super(http);
    // Substitui o setter do url para usar relatoriosURL ao invés de managerURL
    const originalUrlSetter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(this), 'url')?.set;
    Object.defineProperty(this, 'url', {
      get: () => (this as any)._url,
      set: (route: string) => {
        (this as any)._url = `${environment.relatoriosURL}${route}`;
      },
      configurable: true
    });
    this.url = `/relatorios`;
  }

  getRelatorioDiosNaoExecutadas(filters?: any): Observable<Data<Dio>> {
    const params = new URLSearchParams();
    if (filters)
      for (const key in filters)
          params.append(key, filters[key]);

    return this.http.get<Data<Dio>>(`${this.url}/dios-nao-executados?${params}`);
  }

  getRelatorioDiosAferidas(filters?: any, page: number = 0, pageSize: number = 10): Observable<Data<Dio>> {
    const params = new URLSearchParams();
    if (filters)
      for (const key in filters)
          params.append(key, filters[key]);
    params.append('page', `${page}`);
    params.append('pageSize', `${pageSize}`);

    return this.http.get<Data<Dio>>(`${this.url}/dios-aferidas?${params}`);
  }

  getRelatorioConsumoPorEquipamento(filters?: any, page: number = 0, pageSize: number = 10): Observable<Data<DioDetalhesExecucao>> {
    const params = new URLSearchParams();
    if (filters)
      for (const key in filters)
        params.append(key, filters[key]);
    params.append('page', `${page}`);
    params.append('pageSize', `${pageSize}`);

    return this.http.get<any>(`${this.url}/consumo-por-equipamento?${params}`);
  }

  getValoresBrutos(filters: VBBFilters, pagination: GenerateTablePagination): Observable<Data<Dio>> {
    const params = this.newParameters({ ...filters, ...pagination });
    return this.http.get<any>(`${this.url}/dios/valores-brutos?${params}`);
  }

  getValoresBrutosEquipamentos(filters: VBBFilters, pagination: GenerateTablePagination): Observable<Data<Dio>> {
    const params = this.newParameters({ ...filters, ...pagination });
    return this.http.get<any>(`${this.url}/valores-brutos-equipamentos?${params}`);
  }

  getEquipamentosExistentes(page: number, pageSize: number, filters: any): Observable<Data<any>> {
    const params = new URLSearchParams();
    params.append('page', `${page}`);
    params.append('pageSize', `${pageSize}`);

    if (filters)
      for (const key in filters)
          params.append(key, filters[key]);

    return this.http.get<any>(`${this.url}/equipamentos?${params}`);
  }

  downloadArquivoCsvOrPdf(filters: VBBFilters, endpointRelatorio: string, type: string = 'csv') {
    const params = this.newParameters(filters);
    return this.http.get<any>(`${this.url}/${endpointRelatorio}/${type}?${params}`, { observe: 'response', responseType: 'blob' as 'json' });
  }

  getRelatorioAfericao(filters: any, page: number = 0, pageSize: number = 10): Observable<Data<any>> {
    const params = this.newParameters({ ...filters });
    params.append('page', `${page}`);
    params.append('pageSize', `${pageSize}`);
    return this.http.get<any>(`${this.url}/relatorios-afericao?${params}`);
  }

  getRelatorioAprovacao(filters: any): Observable<Data<any>> {
    const params = this.newParameters({ ...filters });
    return this.http.get<any>(`${this.url}/relatorios-aprovacao?${params}`);
  }

  getRelatorioPrefixosSemDio(beneficiariaId: string, dataOperacao: Date): Observable<string[]> {
    const dataFormatada = dataOperacao.toISOString().split('T')[0]; // YYYY-MM-DD
    const params = new URLSearchParams();
    params.append('beneficiaria', beneficiariaId);
    params.append('dataOperacao', dataFormatada);
    return this.http.get<string[]>(`${environment.relatoriosURL}/relatorio-prefixo-sem-dio?${params}`);
  }

  getRelatorioDashboardBeneficiaria(beneficiariaId: string, dataOperacao: Date): Observable<any[]> {
    const dataFormatada = dataOperacao.toISOString().split('T')[0]; // yyyy-MM-DD
    const params = new URLSearchParams();
    params.append('dataOperacao', dataFormatada);
    return this.http.get<any[]>(`${this.url}/dashboard-beneficiaria/${beneficiariaId}?${params}`);
  }

  getRelatorioDiosPorPrefixos(filters: any, page: number, pageSize: number): Observable<string[]> {
    const params = this.newParameters({ ...filters });
    params.append('page', `${page}`);
    params.append('pageSize', `${pageSize}`);
    return this.http.get<string[]>(`${this.url}/dios-por-prefixo?${params}`);
  }

  getRelatorioUsuariosPorBeneficiaria(filters: any, page: number, pageSize: number): Observable<string[]> {
    const params = this.newParameters({ ...filters });
    params.append('page', `${page}`);
    params.append('pageSize', `${pageSize}`);

    return this.http.get<string[]>(`${this.url}/usuarios-por-beneficiaria?${params}`);
  }

  getRelatorioDiosEncerradosAutomaticamente(filters: any): Observable<any[]> {
    const params = this.newParameters({ ...filters });
    return this.http.get<any[]>(`${this.url}/dios-encerrados-automaticamente?${params}`);
  }

  getPendenciasDeFaturamento(page: number, pageSize: number, filters: any): Observable<any> {
    const contratoId = filters.contrato;

    // Contrato enviado como path param 
    filters.contrato = undefined;
    filters.beneficiaria = undefined;

    const params = this.newParameters(filters);
    params.append('page', `${page}`);
    params.append('pageSize', `${pageSize}`);

    return this.http.get<any>(`${this.url}/contratos/${contratoId}/pendentes-de-faturamento?${params}`);
  }

  newParameters(filters: any): URLSearchParams {
    const params = new URLSearchParams();

    if (filters) {      
      for (const key in filters) {
        const value = filters[key];
        // Permite valores booleanos (false), mas ignora null, undefined e string vazia
        if (value !== '' && value !== null && value !== undefined) {
          params.append(key, filters[key]);
        }
      }
    }

    return params;
  }

}